import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ActionLogItem,
  ActionLogSearchRequest,
  ActionLogSearchRequestSchema,
  ActionLogSearchResponse,
  ActionLogSearchResponseSchema,
  ActionLogSetStatusRequest,
  ActionLogStatus,
  ActionStatusUpdateResponse,
  ActionStatusUpdateResponseSchema,
  CreateActionLogRequest,
  CreateActionLogRequestSchema,
  CreateActionLogResponse,
  CreateActionLogResponseSchema,
} from '../../models/action-log.model';
import { ActionLogStore } from '../../stores/action-log/action-log.store';
import { ClientStore } from '../../stores/client/client.store';
import { SmartrService } from '../smartr.service';

@Injectable({ providedIn: 'root' })
export class ActionLogService extends SmartrService {
  private readonly http = inject(HttpClient);
  private readonly clientStore = inject(ClientStore);
  private readonly actionLogStore = inject(ActionLogStore);

  /**
   * Creates a new action log entry.
   * @param payload The request body to create an action log.
   * @returns An observable emitting the created ActionId (UUID string).
   */
  create(payload: CreateActionLogRequest): Observable<CreateActionLogResponse> {
    const parsed = CreateActionLogRequestSchema.parse(payload);

    return this.http.post<CreateActionLogResponse>(environment.actionLog.create, parsed).pipe(
      this.verifyResponse(CreateActionLogResponseSchema),
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(`Failed to create action log: ${error.message}`))
      )
    );
  }

  /**
   * Searches for action logs based on the provided criteria.
   * @param payload The search criteria for action logs.
   * @returns An observable containing the search results.
   */
  search(payload: ActionLogSearchRequest): Observable<ActionLogSearchResponse> {
    const parsed = ActionLogSearchRequestSchema.parse(payload);
    return this.http.post<ActionLogSearchResponse>(environment.actionLog.search, parsed).pipe(
      this.verifyResponse(ActionLogSearchResponseSchema),
      map((resp: ActionLogSearchResponse) => resp),
      tap((resp: ActionLogSearchResponse) => {
        if (resp) {
          this.actionLogStore.setActions(resp as ActionLogItem[]);
        }
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * Updates the status of an action log entry.
   * @param actionId The ID of the action log entry to update.
   * @param status The new status to set for the action log entry.
   * @param message An optional message to include with the status update.
   * @param clientId The ID of the client associated with the action log entry.
   * @returns An observable containing the response from the API.
   */
  updateStatus(
    actionId: string,
    status: (typeof ActionLogStatus)[keyof typeof ActionLogStatus],
    message: string,
    clientId?: number
  ): Observable<ActionStatusUpdateResponse> {
    const fallbackClientId = clientId || this.clientStore.client?.()?.clientId;
    if (!fallbackClientId) {
      return throwError(() => new Error('Client ID is required for action log update'));
    }

    const brokerageId = this.clientStore.client?.()?.brokerageId;
    if (typeof brokerageId !== 'string') {
      return throwError(() => new Error('Brokerage ID is required for action log update'));
    }

    const request: ActionLogSetStatusRequest = {
      organisationId: brokerageId,
      actionId: actionId,
      newStatus: status,
      newMessage: message,
      userId: fallbackClientId,
    };

    return this.http.put<ActionStatusUpdateResponse>(environment.actionLog.update, request).pipe(
      this.verifyResponse(ActionStatusUpdateResponseSchema),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => new Error(`Failed to update action status: ${error.message}`));
      })
    );
  }
}
